import Phaser from 'phaser';
import type { GameState, Die } from '../types';
import { COLORS } from '../main';
import { makeDieSprite } from './diceRender';

export interface HandViewOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  onDieTap: (die: Die) => void;
  onAdjust: (die: Die, delta: 1 | -1) => void;
  onReroll: (die: Die) => void;
  onUseToken: (token: 'reroll' | 'convertColor', die: Die) => void;
}

export class HandView {
  scene: Phaser.Scene;
  container: Phaser.GameObjects.Container;
  options: HandViewOptions;
  selectedDieId: string | null = null;

  constructor(scene: Phaser.Scene, options: HandViewOptions) {
    this.scene = scene;
    this.options = options;
    this.container = scene.add.container(options.x, options.y);
    const bg = scene.add.rectangle(
      options.width / 2,
      options.height / 2,
      options.width,
      options.height,
      COLORS.panel
    );
    bg.setStrokeStyle(1, COLORS.border);
    this.container.add(bg);
  }

  setSelectedDie(id: string | null): void {
    this.selectedDieId = id;
  }

  refresh(state: GameState): void {
    // Clear children except the bg (index 0)
    const bg = this.container.getAt(0);
    this.container.removeAll(false);
    if (bg) this.container.add(bg);

    const { hand } = state;
    const dieSize = Math.min(this.options.height * 0.6, this.options.width / 5);
    const slotGap = 8;
    const totalWidth = 3 * dieSize + 2 * slotGap;
    const startX = (this.options.width - totalWidth) / 2 + dieSize / 2;
    const slotY = dieSize / 2 + 8;

    for (let i = 0; i < 3; i++) {
      const x = startX + i * (dieSize + slotGap);
      const die = hand[i];
      if (die) {
        const isSelected = die.id === this.selectedDieId;
        const dg = makeDieSprite(this.scene, x, slotY, dieSize, die);
        if (isSelected) dg.bg.setStrokeStyle(3, COLORS.accent);
        dg.bg.setInteractive({ useHandCursor: true });
        dg.bg.on('pointerdown', () => this.options.onDieTap(die));
        this.container.add(dg.container);
      } else {
        const placeholder = this.scene.add
          .rectangle(x, slotY, dieSize, dieSize, COLORS.bg)
          .setStrokeStyle(1, COLORS.border);
        this.container.add(placeholder);
      }
    }

    // Status / perks line
    const statusY = dieSize + 22;
    const status = this.scene.add
      .text(this.options.width / 2, statusY, this.statusText(state), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#9ca3af',
        align: 'center',
      })
      .setOrigin(0.5, 0);
    this.container.add(status);

    // Perk buttons (only if a die is selected and perks remain)
    const selected = state.hand.find((d) => d.id === this.selectedDieId);
    if (selected) {
      const buttonsY = statusY + 24;
      const btns: Array<{ label: string; cb: () => void; enabled: boolean }> = [];
      if (state.perksRemaining.adjust > 0) {
        btns.push({
          label: '−1',
          cb: () => this.options.onAdjust(selected, -1),
          enabled: selected.value != null && selected.value > 1,
        });
        btns.push({
          label: '+1',
          cb: () => this.options.onAdjust(selected, 1),
          enabled: selected.value != null && selected.value < selected.faces,
        });
      }
      if (state.perksRemaining.reroll > 0) {
        btns.push({
          label: 'reroll',
          cb: () => this.options.onReroll(selected),
          enabled: true,
        });
      }
      if ((state.tokens.reroll ?? 0) > 0) {
        btns.push({
          label: 'tk:reroll',
          cb: () => this.options.onUseToken('reroll', selected),
          enabled: true,
        });
      }
      const btnW = 56;
      const btnH = 28;
      const gap = 6;
      const totalBtnW = btns.length * btnW + (btns.length - 1) * gap;
      let bx = (this.options.width - totalBtnW) / 2 + btnW / 2;
      for (const b of btns) {
        const fill = b.enabled ? COLORS.accent : COLORS.cardAcquired;
        const rect = this.scene.add
          .rectangle(bx, buttonsY + btnH / 2, btnW, btnH, fill)
          .setStrokeStyle(1, COLORS.border);
        if (b.enabled) {
          rect.setInteractive({ useHandCursor: true }).on('pointerdown', b.cb);
        }
        const txt = this.scene.add
          .text(bx, buttonsY + btnH / 2, b.label, {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '12px',
            fontStyle: 'bold',
            color: '#0d1117',
          })
          .setOrigin(0.5);
        this.container.add([rect, txt]);
        bx += btnW + gap;
      }
    }
  }

  private statusText(state: GameState): string {
    const parts: string[] = [];
    parts.push(`Phase: ${state.phase}`);
    if (state.perksRemaining.adjust > 0) parts.push(`±1 ×${state.perksRemaining.adjust}`);
    if (state.perksRemaining.reroll > 0) parts.push(`↻ ×${state.perksRemaining.reroll}`);
    if (this.selectedDieId) parts.push('(tap a card to place)');
    return parts.join('  ·  ');
  }
}
